
import React, { useState, useMemo, useCallback, useRef } from 'react';
import { InventoryItem, LabelSize, Employee, EmployeeRole, TradedInDevice, BuybackRequest, AppSettings } from '../types';
import { Button } from './Button';
import { PrintableInventoryLabel } from './PrintableInventoryLabel';
import { ServiceItemModal } from './ServiceItemModal';
import { DYMO_LABEL_SIZES, DEFAULT_LABEL_SIZE_ID } from '../constants';
import { PrintableTradeInAgreement } from './PrintableTradeInAgreement';


interface InventoryPageProps {
  inventoryItems: InventoryItem[];
  onImportSquareInventory: (parsedItems: Partial<InventoryItem>[]) => Promise<void>;
  importLoading: boolean;
  lastInventoryImportTime?: string;
  onOpenPrintModal: (title: string, content: React.ReactNode, contentClassName?: string) => void;
  onAddServiceItem: (itemData: Omit<InventoryItem, 'id' | 'lastSyncedAt' | 'source'>) => void;
  onUpdateServiceItem: (itemId: string, itemData: Omit<InventoryItem, 'id' | 'lastSyncedAt' | 'source'>) => void;
  onDeleteServiceItem: (itemId: string) => void;
  currentUser: Employee | null;
  appSettings: AppSettings;
  tradedInDevices: TradedInDevice[];
  buybackRequests: BuybackRequest[];
}

// Icons
const UploadCloudIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={`w-4 h-4 ${props.className?.includes('animate-spin') ? '' : 'mr-1.5'}`} {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.838-2.298c1.01.198 1.904.738 2.548 1.44a3.75 3.75 0 01.296 5.438" />
  </svg>
);
const CubeIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 mr-2 text-[var(--color-primary)]" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9" />
  </svg>
);
const TagIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20" strokeWidth={1.5} stroke="currentColor" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
  </svg>
);
const PlusCircleIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);
const ArrowPathIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
  </svg>
);


export const InventoryPage: React.FC<InventoryPageProps> = ({
  inventoryItems,
  onImportSquareInventory,
  importLoading,
  lastInventoryImportTime,
  onOpenPrintModal,
  onAddServiceItem,
  onUpdateServiceItem,
  onDeleteServiceItem,
  currentUser,
  appSettings,
  tradedInDevices,
  buybackRequests,
}) => {
  const [activeTab, setActiveTab] = useState<'service_general' | 'service_repair' | 'square' | 'traded_in'>('service_general');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [itemToEdit, setItemToEdit] = useState<InventoryItem | null>(null);
  const [selectedLabelSizeId, setSelectedLabelSizeId] = useState<string>(DEFAULT_LABEL_SIZE_ID);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [importMessage, setImportMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isUserInactive = currentUser?.status === 'Inactive';
  const canManageServiceItemsRoles: EmployeeRole[] = ['Administrator', 'Lead Technician', 'Senior Technician'];
  const canManageServiceItems = currentUser && canManageServiceItemsRoles.includes(currentUser.role as EmployeeRole) && !isUserInactive;


  const generalServiceInventory = useMemo(() => inventoryItems.filter(item => item.source === 'service' && item.usageType === 'general'), [inventoryItems]);
  const repairServiceInventory = useMemo(() => inventoryItems.filter(item => item.source === 'service' && item.usageType === 'repair'), [inventoryItems]);
  const squareInventory = useMemo(() => inventoryItems.filter(item => item.source === 'square'), [inventoryItems]);

  const openAddItemModal = () => {
    if (!canManageServiceItems) {
        alert("You do not have permission to add service items.");
        return;
    }
    setItemToEdit(null);
    setIsModalOpen(true);
  };

  const openEditItemModal = (item: InventoryItem) => {
    if (!canManageServiceItems) {
        alert("You do not have permission to edit service items.");
        return;
    }
    setItemToEdit(item);
    setIsModalOpen(true);
  };

  const handleSaveServiceItem = (itemData: Omit<InventoryItem, 'id' | 'lastSyncedAt' | 'source'>, id?: string) => {
    if (id) {
      onUpdateServiceItem(id, itemData);
    } else {
      onAddServiceItem(itemData);
    }
    setIsModalOpen(false);
  };

  const handleDeleteConfirmed = (itemId: string) => {
    if (!canManageServiceItems) {
        alert("You do not have permission to delete service items.");
        return;
    }
    if (window.confirm(`Are you sure you want to delete item ${itemId}? This action cannot be undone.`)) {
        onDeleteServiceItem(itemId);
    }
  };

  const formatCurrency = (amount?: number) => {
    if (typeof amount !== 'number') return 'N/A';
    return `$${amount.toFixed(2)}`;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const handlePrintInventoryLabel = (item: InventoryItem) => {
    const labelSize = DYMO_LABEL_SIZES.find(s => s.id === selectedLabelSizeId) || DYMO_LABEL_SIZES[0];
    onOpenPrintModal(
      `Inventory Label - ${item.sku || item.name}`,
      <PrintableInventoryLabel item={item} labelSizeId={selectedLabelSizeId} />,
      `label-print-base ${labelSize.printClassName}`
    );
  };

  const handlePrintTradeInAgreement = (tradedInDevice: TradedInDevice) => {
    const originalRequest = buybackRequests.find(req => req.id === tradedInDevice.buybackRequestId);
    if (!originalRequest) {
        alert("Original buyback request not found for this device.");
        return;
    }
    onOpenPrintModal(
        `Trade-In Agreement - ${originalRequest.id}`,
        <PrintableTradeInAgreement buybackRequest={originalRequest} appSettings={appSettings} />,
        'printable-area' // Use a more generic class for full-page agreements
    );
  };


  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setCsvFile(event.target.files[0]);
      setImportMessage(null);
    } else {
      setCsvFile(null);
    }
  };

  const handleImportCsvTrigger = async () => {
    if (!canManageServiceItems) {
        setImportMessage({ type: 'error', text: 'You do not have permission to import inventory.' });
        return;
    }
    if (!csvFile) {
      setImportMessage({ type: 'error', text: 'Please select a CSV file to import.' });
      return;
    }
    setImportMessage(null);

    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = e.target?.result as string;
      if (!text) {
        setImportMessage({ type: 'error', text: 'Could not read the file content.' });
        return;
      }
      try {
        const parsedData = parseCsv(text);
        await onImportSquareInventory(parsedData);
        setCsvFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      } catch (error) {
        console.error("Error processing CSV:", error);
        setImportMessage({ type: 'error', text: `Error processing CSV: ${error instanceof Error ? error.message : String(error)}` });
      }
    };
    reader.onerror = () => {
      setImportMessage({ type: 'error', text: 'Error reading the file.' });
    };
    reader.readAsText(csvFile);
  };

  const parseCsv = (csvText: string): Partial<InventoryItem>[] => {
    const lines = csvText.split(/\r\n|\n/).filter(line => line.trim() !== '');
    if (lines.length < 2) throw new Error("CSV must contain at least a header row and one data row.");

    const headers = lines[0].split(',').map(header => header.trim().toLowerCase());
    const data: Partial<InventoryItem>[] = [];

    const skuIndex = headers.findIndex(h => h === 'token' || h === 'sku');
    const nameIndex = headers.findIndex(h => h === 'item name' || h === 'name');
    const categoryIndex = headers.findIndex(h => h === 'category name' || h === 'category');
    const quantityHeaderIndex = headers.findIndex(h => h.startsWith('current quantity'));
    const priceIndex = headers.findIndex(h => h === 'price');
    const costIndex = headers.findIndex(h => h === 'cost price' || h === 'cost');

    if (nameIndex === -1) throw new Error("CSV must contain 'Item Name' or 'Name' header.");

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      const item: Partial<InventoryItem> = { source: 'square', usageType: 'sale' };

      if (nameIndex !== -1 && values[nameIndex]) item.name = values[nameIndex];
      else continue;

      if (skuIndex !== -1 && values[skuIndex]) item.sku = values[skuIndex];
      if (categoryIndex !== -1 && values[categoryIndex]) item.category = values[categoryIndex];

      if (quantityHeaderIndex !== -1 && values[quantityHeaderIndex]) {
        const stockVal = parseFloat(values[quantityHeaderIndex]);
        if (!isNaN(stockVal)) item.stock = stockVal;
      }
      if (priceIndex !== -1 && values[priceIndex]) {
        const priceVal = parseFloat(values[priceIndex].replace('$', ''));
        if (!isNaN(priceVal)) item.price = priceVal;
      }
      if (costIndex !== -1 && values[costIndex]) {
        const costVal = parseFloat(values[costIndex].replace('$', ''));
        if (!isNaN(costVal)) item.cost = costVal;
      }
      data.push(item);
    }
    return data;
  };

  const renderInventoryTable = (items: InventoryItem[], itemType: 'service_general' | 'service_repair' | 'square') => {
    const isServiceType = itemType === 'service_general' || itemType === 'service_repair';
    return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200">
        <thead className="bg-slate-50">
          <tr>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Name</th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">SKU</th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Category</th>
            {isServiceType && <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Usage</th>}
            <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">Stock</th>
            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Cost</th>
            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Price</th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Supplier</th>
            {isServiceType && <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Notes</th>}
            {!isServiceType && <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Last Imported</th>}
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-slate-200">
          {items.length === 0 && (
            <tr>
              <td colSpan={isServiceType ? 10 : 9} className="px-6 py-10 text-center text-sm text-slate-500">
                No items found in this section.
              </td>
            </tr>
          )}
          {items.map((item) => (
            <tr key={item.id} className="hover:bg-slate-50 transition-colors">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">{item.name}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">{item.sku || 'N/A'}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">{item.category || 'N/A'}</td>
              {isServiceType && <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 capitalize">{item.usageType || 'N/A'}</td>}
              <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700 font-semibold text-center">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${item.stock <= 5 ? 'bg-red-100 text-red-700' : item.stock <=15 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                  {item.stock}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 text-right">{formatCurrency(item.cost)}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 text-right">{formatCurrency(item.price)}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">{item.supplier || 'N/A'}</td>
              {isServiceType && <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 truncate max-w-xs" title={item.notes}>{item.notes || 'N/A'}</td>}
              {!isServiceType && <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">{formatDate(item.lastSyncedAt)}</td>}
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                <Button variant="ghost" onClick={() => handlePrintInventoryLabel(item)} className="text-slate-600 hover:text-[var(--color-primary)] text-xs p-1 flex items-center group !min-w-0 !px-1.5 !py-0.5">
                   <TagIcon className="text-slate-400 group-hover:text-[var(--color-primary)] w-3.5 h-3.5 mr-1" /> Print
                </Button>
                {isServiceType && (
                  <>
                    <Button variant="ghost" onClick={() => openEditItemModal(item)} className="text-slate-600 hover:text-[var(--color-primary)] text-xs p-1 !min-w-0 !px-1.5 !py-0.5" disabled={!canManageServiceItems} title={!canManageServiceItems ? "Permission Denied" : "Edit Item"}>Edit</Button>
                    <Button variant="ghost" onClick={() => handleDeleteConfirmed(item.id)} className="text-red-500 hover:text-red-700 text-xs p-1 !min-w-0 !px-1.5 !py-0.5" disabled={!canManageServiceItems} title={!canManageServiceItems ? "Permission Denied" : "Delete Item"}>Delete</Button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )};

  const renderTradedInDevicesTable = () => (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200">
        <thead className="bg-slate-50">
          <tr>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">ID</th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Device</th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Condition</th>
            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Trade Value</th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Date Added</th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-slate-200">
          {tradedInDevices.length === 0 && (
            <tr>
              <td colSpan={7} className="px-6 py-10 text-center text-sm text-slate-500">
                No traded-in devices found.
              </td>
            </tr>
          )}
          {tradedInDevices.map((device) => (
            <tr key={device.id} className="hover:bg-slate-50 transition-colors">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">{device.id}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                {device.deviceType} - {device.deviceModelName}
                {device.imeiOrSerial && <span className="block text-xs text-slate-400">SN/IMEI: {device.imeiOrSerial}</span>}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">{device.overallConditionAtTradeIn}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 text-right">{formatCurrency(device.finalTradeInValue)}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">{device.currentStatus}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">{formatDate(device.dateAddedToInventory)}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <Button
                  variant="ghost"
                  onClick={() => handlePrintTradeInAgreement(device)}
                  className="text-slate-600 hover:text-[var(--color-primary)] text-xs p-1 flex items-center group !min-w-0 !px-1.5 !py-0.5"
                  title="Print Trade-in Agreement"
                >
                  Print Agreement
                </Button>
                {/* Future actions: Edit Status, View Details, etc. */}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );


  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="flex items-center mb-1">
            <CubeIcon />
            <h1 className="text-2xl font-bold text-[var(--color-primary)]">Inventory Management</h1>
        </div>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <p className="text-sm text-slate-600">Manage service parts, repair items, and Square product stock.</p>
          <div className="flex items-center gap-2">
            <label htmlFor="label-size-select" className="text-sm font-medium text-slate-700 whitespace-nowrap">Label Size:</label>
            <select
              id="label-size-select"
              value={selectedLabelSizeId}
              onChange={(e) => setSelectedLabelSizeId(e.target.value)}
              className="px-3 py-1.5 border border-slate-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] text-xs"
              aria-label="Select label size for printing"
            >
              {DYMO_LABEL_SIZES.map(size => (
                <option key={size.id} value={size.id}>{size.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md">
        <div className="border-b border-slate-200">
          <nav className="flex space-x-1 p-2 flex-wrap" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('service_general')}
              className={`px-4 py-2.5 font-medium text-sm rounded-md transition-colors
                ${activeTab === 'service_general' ? 'bg-[var(--color-primary)] text-white shadow-sm' : 'text-slate-600 hover:text-[var(--color-primary)] hover:bg-slate-100'}`}
              aria-current={activeTab === 'service_general' ? 'page' : undefined}
            >
              Service Parts (General)
            </button>
            <button
              onClick={() => setActiveTab('service_repair')}
              className={`px-4 py-2.5 font-medium text-sm rounded-md transition-colors
                ${activeTab === 'service_repair' ? 'bg-[var(--color-primary)] text-white shadow-sm' : 'text-slate-600 hover:text-[var(--color-primary)] hover:bg-slate-100'}`}
              aria-current={activeTab === 'service_repair' ? 'page' : undefined}
            >
              Repair Service Items
            </button>
            <button
              onClick={() => setActiveTab('square')}
              className={`px-4 py-2.5 font-medium text-sm rounded-md transition-colors
                ${activeTab === 'square' ? 'bg-[var(--color-primary)] text-white shadow-sm' : 'text-slate-600 hover:text-[var(--color-primary)] hover:bg-slate-100'}`}
              aria-current={activeTab === 'square' ? 'page' : undefined}
            >
              Square Stock (Sales)
            </button>
             <button
              onClick={() => setActiveTab('traded_in')}
              className={`px-4 py-2.5 font-medium text-sm rounded-md transition-colors
                ${activeTab === 'traded_in' ? 'bg-[var(--color-primary)] text-white shadow-sm' : 'text-slate-600 hover:text-[var(--color-primary)] hover:bg-slate-100'}`}
              aria-current={activeTab === 'traded_in' ? 'page' : undefined}
            >
              Traded-in Devices ({tradedInDevices.length})
            </button>
          </nav>
        </div>

        <div className="p-4">
          {activeTab === 'service_general' && (
            <div>
              <div className="flex justify-end mb-4">
                <Button variant="primary" onClick={openAddItemModal} disabled={!canManageServiceItems} title={!canManageServiceItems ? "Permission Denied" : "Add New General Item"}>
                  <PlusCircleIcon /> Add General Item
                </Button>
              </div>
              {renderInventoryTable(generalServiceInventory, 'service_general')}
            </div>
          )}
          {activeTab === 'service_repair' && (
             <div>
              <div className="flex justify-end mb-4">
                <Button variant="primary" onClick={openAddItemModal} disabled={!canManageServiceItems} title={!canManageServiceItems ? "Permission Denied" : "Add New Repair Item"}>
                  <PlusCircleIcon /> Add Repair Item
                </Button>
              </div>
              {renderInventoryTable(repairServiceInventory, 'service_repair')}
            </div>
          )}
          {activeTab === 'square' && (
            <div>
              <div className="mb-6 p-4 border border-dashed border-slate-300 rounded-md bg-slate-50">
                <h3 className="text-lg font-semibold text-slate-700 mb-2">Square Inventory CSV Import</h3>
                <p className="text-xs text-slate-600 mb-2">
                  Import your Square inventory items from a CSV file. Required columns: 'Item Name' or 'Name'.
                  Optional columns: 'Token' or 'SKU', 'Category Name' or 'Category', 'Current Quantity...', 'Price', 'Cost Price' or 'Cost'.
                </p>
                <div className="flex flex-col sm:flex-row items-center gap-3">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    ref={fileInputRef}
                    className="block w-full max-w-xs text-sm text-slate-500 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-sky-100 file:text-sky-700 hover:file:bg-sky-200 disabled:opacity-50"
                    disabled={importLoading || !canManageServiceItems}
                  />
                  <Button
                    variant="secondary"
                    onClick={handleImportCsvTrigger}
                    disabled={importLoading || !csvFile || !canManageServiceItems}
                    className="w-full sm:w-auto flex items-center justify-center !text-black"
                    title={!canManageServiceItems ? "Permission Denied" : (importLoading ? "Importing..." : "Import Square CSV")}
                  >
                    {importLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Importing...
                      </>
                    ) : (
                      <><UploadCloudIcon /> Import Square CSV</>
                    )}
                  </Button>
                </div>
                {importMessage && (
                  <p className={`mt-2 text-xs ${importMessage.type === 'success' ? 'text-emerald-600' : 'text-red-600'}`}>{importMessage.text}</p>
                )}
                 <p className="text-xs text-slate-500 mt-2">Last import: {formatDate(lastInventoryImportTime)}</p>
              </div>
              {renderInventoryTable(squareInventory, 'square')}
            </div>
          )}
          {activeTab === 'traded_in' && renderTradedInDevicesTable()}
        </div>
      </div>

      {isModalOpen && (
        <ServiceItemModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSaveServiceItem}
          itemToEdit={itemToEdit}
        />
      )}
    </div>
  );
};
